/**
 * First-person controller.
 *
 * Every compiled solid is an axis-aligned box, which makes collision a matter
 * of resolving one axis at a time against a spatial hash — no physics engine,
 * no convex decomposition, no tunnelling. That is a direct dividend of the
 * compiler owning geometry: the runtime gets to assume things about it.
 */

import * as THREE from 'three';

export type MoveMode = 'walk' | 'fly';

export interface Collider {
  box: THREE.Box3;
  id: string;
  dynamic: boolean;
  /**
   * Furniture, rather than architecture.
   *
   * The navmesh is baked from the level's own solids and knows nothing about
   * the props built on top of them, so anything that steers by the mesh must
   * not be stopped by one — an enemy following a route through a room would
   * wedge on a crate the route runs straight through. The player steers by
   * eye and should be stopped by it, and bullets should stop too. So it is
   * solid for everything except the things that navigate.
   */
  soft?: boolean;
}

const GRAVITY = 20;
const JUMP_SPEED = 6.0;
const SPEED = { walk: 5.0, sprint: 8.4, crouch: 2.4, fly: 14, flyFast: 42 };
const EYE = { stand: 1.62, crouch: 1.0 };
/**
 * Time constant for the camera catching up after the feet rise, in seconds.
 * Exponential rather than a fixed rate: a fixed rate drains the offset faster
 * than a slope feeds it, so walking up a ramp turns into a saw-tooth.
 */
const STEP_SMOOTH_TAU = 0.08;

export class Player {
  readonly camera: THREE.PerspectiveCamera;
  /** Feet position, Three.js frame. */
  pos = new THREE.Vector3();
  velocity = new THREE.Vector3();
  yaw = 0;
  pitch = 0;
  mode: MoveMode = 'walk';
  grounded = false;
  crouching = false;
  radius = 0.35;
  height = 1.8;
  step = 0.45;
  /** Solids whose collision is disabled — the breach demo. */
  ignore = new Set<string>();
  /** Visual lag applied after a step up, so the eye rises smoothly. */
  private stepOffset = 0;
  /**
   * Below this the player is out of the world and gets put back. Collision on
   * a grid of boxes will always have corner cases; a level should never be
   * able to swallow you because of one.
   */
  killPlane = -Infinity;
  private lastSafe = new THREE.Vector3();
  private hasSafe = false;
  /** Incremented whenever the kill plane caught the player. */
  rescues = 0;
  /** Multiplier on walking speed, so run upgrades can change it. */
  speedScale = 1;
  /**
   * Analog movement from a touch stick, in the same basis as WASD:
   * +y forward, +x right. Added to whatever the keys are doing.
   */
  analog: { x: number; y: number } | null = null;
  /** One-shot jump, for inputs that are not a held key. */
  private jumpQueued = false;

  private keys = new Set<string>();
  private cells = new Map<string, Collider[]>();
  private cellSize = 4;
  private colliders: Collider[] = [];

  constructor(camera: THREE.PerspectiveCamera) {
    this.camera = camera;
  }

  setColliders(colliders: Collider[]): void {
    this.colliders = colliders;
    this.cells.clear();
    for (const c of colliders) {
      const x0 = Math.floor(c.box.min.x / this.cellSize);
      const x1 = Math.floor(c.box.max.x / this.cellSize);
      const z0 = Math.floor(c.box.min.z / this.cellSize);
      const z1 = Math.floor(c.box.max.z / this.cellSize);
      for (let x = x0; x <= x1; x++)
        for (let z = z0; z <= z1; z++) {
          const k = `${x},${z}`;
          let arr = this.cells.get(k);
          if (!arr) this.cells.set(k, (arr = []));
          arr.push(c);
        }
    }
  }

  get colliderCount(): number {
    return this.colliders.length;
  }

  private near(): Collider[] {
    const out: Collider[] = [];
    const x = Math.floor(this.pos.x / this.cellSize);
    const z = Math.floor(this.pos.z / this.cellSize);
    for (let i = -1; i <= 1; i++)
      for (let j = -1; j <= 1; j++) {
        const arr = this.cells.get(`${x + i},${z + j}`);
        if (arr) out.push(...arr);
      }
    return out;
  }

  onKey(e: KeyboardEvent, down: boolean): void {
    const code = e.code;
    if (down) this.keys.add(code);
    else this.keys.delete(code);
  }

  clearKeys(): void {
    this.keys.clear();
  }

  requestJump(): void {
    this.jumpQueued = true;
  }

  /** Some people fly aeroplanes. */
  invertY = false;

  look(dx: number, dy: number, sensitivity = 0.0022): void {
    this.yaw -= dx * sensitivity;
    this.pitch -= dy * sensitivity * (this.invertY ? -1 : 1);
    const lim = Math.PI / 2 - 0.01;
    this.pitch = Math.max(-lim, Math.min(lim, this.pitch));
  }

  teleport(p: THREE.Vector3): void {
    this.pos.copy(p);
    this.velocity.set(0, 0, 0);
    this.stepOffset = 0;
    this.lastSafe.copy(p);
    this.hasSafe = true;
  }

  private get currentHeight(): number {
    return this.crouching ? this.height * 0.62 : this.height;
  }

  private overlaps(near: Collider[]): Collider[] {
    const h = this.currentHeight;
    const min = new THREE.Vector3(this.pos.x - this.radius, this.pos.y, this.pos.z - this.radius);
    const max = new THREE.Vector3(this.pos.x + this.radius, this.pos.y + h, this.pos.z + this.radius);
    const hits: Collider[] = [];
    for (const c of near) {
      if (this.ignore.has(c.id)) continue;
      if (
        c.box.max.x > min.x &&
        c.box.min.x < max.x &&
        c.box.max.y > min.y &&
        c.box.min.y < max.y &&
        c.box.max.z > min.z &&
        c.box.min.z < max.z
      )
        hits.push(c);
    }
    return hits;
  }

  /**
   * Stand on a step you are already inside, instead of fighting it.
   *
   * The bake and the body disagree about what a step is, and this is where
   * they are reconciled. `bakeNavmesh` ignores anything within a step of a
   * surface — that is the rule that makes a stair tread walkable rather than a
   * wall — so it will happily put a standable sample on the floor at the foot
   * of a staircase whose treads overhang it. The body has no such rule: it is
   * an upright box, the tread box is inside it, every horizontal move resolves
   * back out, and the step-up retry asks for a position with no overlaps at
   * all, which there is not. The result is a player who cannot move in any
   * direction, at a place the navmesh has already promised is walkable and the
   * flow field is already routing enemies through.
   *
   * `tools/steps.ts` walks every navmesh edge that changes height with the
   * real body. All 44 of the failures across thirty maps were this, and none
   * of them were a step too tall to climb — which is worth stating because the
   * obvious fix, climbing in smaller increments, measured as changing nothing
   * at all.
   *
   * So: if everything the body is inside is low enough to stand on, stand on
   * it. Anything reaching higher is a wall and is left alone, which is what
   * keeps this from being a way to walk up the side of a building.
   */
  private mount(near: Collider[]): void {
    let top = -Infinity;
    for (const c of this.overlaps(near)) {
      // Anything reaching higher than a step is a wall, and a wall next to a
      // step is not a reason to refuse the step. A flight of stairs modelled as
      // boxes all rising from the floor puts five of them inside the body at
      // once, only the nearest of which is climbable.
      const t = c.box.max.y;
      if (t <= this.pos.y + this.step + 1e-3 && t > top) top = t;
    }
    if (top <= this.pos.y) return;
    const was = this.pos.y;
    this.pos.y = top;
    /*
     * Refuse only for want of headroom, and measure it the way the bake does.
     *
     * "Anything above me" is the wrong question on a staircase, where the next
     * four treads are all above you and none of them is a ceiling. The bake
     * asks whether anything occupies the body between a step up and full
     * height, and that is the question here too.
     */
    const roomFrom = this.pos.y + this.step;
    const roomTo = this.pos.y + this.currentHeight;
    if (this.overlaps(near).some((c) => c.box.max.y > roomFrom && c.box.min.y < roomTo)) this.pos.y = was;
    else this.grounded = true;
  }

  /**
   * Move on one horizontal axis and push back out of anything hit.
   *
   * The guard matters: resolution snaps the player to the face of whatever it
   * overlaps, so running it for a move of ~1e-17 (which is what `cos` gives
   * you when you face exactly along an axis) would teleport them the full
   * width of a solid they were merely standing beside.
   */
  private moveAxis(axis: 'x' | 'z', delta: number, near: Collider[]): boolean {
    if (Math.abs(delta) < 1e-6) return false;
    const before = this.pos[axis];
    this.pos[axis] += delta;
    let blocked = false;
    for (const c of this.overlaps(near)) {
      /*
       * A step is not a wall, and this is where that has to be said.
       *
       * Resolving out of everything the body touches means a stair tread
       * inside the foot circle pushes you back off the stair, every frame,
       * for ever. The bake already draws this line — it ignores anything
       * within a step of the surface — and the two have to draw it in the
       * same place or the mesh promises routes the body cannot walk.
       * `mount` does the lifting; this only stops the shoving.
       */
      if (c.box.max.y <= this.pos.y + this.step + 1e-4) continue;
      blocked = true;
      const lo = c.box.min[axis] - this.radius - 1e-4;
      const hi = c.box.max[axis] + this.radius + 1e-4;
      // Push back to the side we came from, not to the side the motion
      // implies. Walls straddle their boundary while floors run right up to
      // it, so a player standing at the very edge of a room is already inside
      // the wall band; resolving by direction of travel ejects them straight
      // through it and out of the map.
      this.pos[axis] = Math.abs(before - lo) <= Math.abs(before - hi) ? lo : hi;
    }
    return blocked;
  }

  /**
   * Move vertically and resolve against the surface actually hit.
   *
   * Resolving against *any* overlapping box is wrong and spectacularly so: a
   * capsule brushing the side of a wall overlaps a box whose top is five
   * metres up and whose bottom is below the floor, and landing then snaps you
   * to the roof while jumping snaps you under the world. So a fall may only
   * land on a surface that was at or below the feet before the move, and a
   * jump may only stop under a surface that was at or above the head.
   */
  private moveVertical(delta: number, near: Collider[]): void {
    if (delta === 0) return;
    const feet = this.pos.y;
    const h = this.currentHeight;
    this.pos.y += delta;

    if (delta < 0) {
      let landing = -Infinity;
      for (const c of this.overlaps(near)) {
        if (c.box.max.y <= feet + 1e-3 && c.box.max.y > landing) landing = c.box.max.y;
      }
      if (landing > -Infinity) {
        this.pos.y = landing;
        this.grounded = true;
        this.velocity.y = 0;
      }
    } else {
      let ceiling = Infinity;
      for (const c of this.overlaps(near)) {
        if (c.box.min.y >= feet + h - 1e-3 && c.box.min.y < ceiling) ceiling = c.box.min.y;
      }
      if (ceiling < Infinity) {
        this.pos.y = ceiling - h - 1e-4;
        this.velocity.y = 0;
      }
    }
  }

  update(dt: number): void {
    // Level basis. `right` is always horizontal: strafing must not inherit the
    // camera's pitch, or looking down turns A and D into a descent.
    const flat = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    const right = new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    const fast = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    this.crouching = this.mode === 'walk' && (this.keys.has('ControlLeft') || this.keys.has('KeyC'));
    const wish = new THREE.Vector3();

    if (this.mode === 'fly') {
      const speed = fast ? SPEED.flyFast : SPEED.fly;
      // Only forward/back follow the camera. Up/down is world up, Unity-style:
      // E rises, Q descends, with Space and C as alternates.
      const look = new THREE.Vector3(
        -Math.sin(this.yaw) * Math.cos(this.pitch),
        Math.sin(this.pitch),
        -Math.cos(this.yaw) * Math.cos(this.pitch),
      );
      if (this.keys.has('KeyW')) wish.add(look);
      if (this.keys.has('KeyS')) wish.sub(look);
      if (this.keys.has('KeyD')) wish.add(right);
      if (this.keys.has('KeyA')) wish.sub(right);
      if (this.analog) {
        wish.addScaledVector(look, this.analog.y);
        wish.addScaledVector(right, this.analog.x);
      }
      if (this.keys.has('KeyE') || this.keys.has('Space')) wish.y += 1;
      if (this.keys.has('KeyQ') || this.keys.has('ControlLeft') || this.keys.has('KeyC')) wish.y -= 1;
      if (wish.lengthSq() > 0) wish.normalize();
      this.velocity.lerp(wish.multiplyScalar(speed), Math.min(1, dt * 12));
      this.pos.addScaledVector(this.velocity, dt);
      this.grounded = false;
    } else {
      const startY = this.pos.y;
      if (this.keys.has('KeyW')) wish.add(flat);
      if (this.keys.has('KeyS')) wish.sub(flat);
      if (this.keys.has('KeyD')) wish.add(right);
      if (this.keys.has('KeyA')) wish.sub(right);
      if (this.analog) {
        wish.addScaledVector(flat, this.analog.y);
        wish.addScaledVector(right, this.analog.x);
      }
      const speed = (this.crouching ? SPEED.crouch : fast ? SPEED.sprint : SPEED.walk) * this.speedScale;
      // Clamp rather than normalise: a stick pushed halfway should walk.
      const mag = wish.length();
      if (mag > 1e-6) wish.multiplyScalar((Math.min(1, mag) * speed) / mag);
      const accel = this.grounded ? 16 : 5;
      this.velocity.x += (wish.x - this.velocity.x) * Math.min(1, dt * accel);
      this.velocity.z += (wish.z - this.velocity.z) * Math.min(1, dt * accel);
      if (this.grounded && (this.keys.has('Space') || this.jumpQueued)) {
        this.velocity.y = JUMP_SPEED;
        this.grounded = false;
      }
      this.jumpQueued = false;
      this.velocity.y -= GRAVITY * dt;
      if (this.velocity.y < -60) this.velocity.y = -60;

      const near = this.near();
      const wasGrounded = this.grounded;
      this.grounded = false;
      this.moveVertical(this.velocity.y * dt, near);

      // Buried in a tread? Stand on it before trying to walk anywhere.
      this.mount(near);

      // Horizontal, with a step-up retry so stairs and kerbs are walkable
      // without a ramp collider. Only while grounded: otherwise holding into a
      // wall in mid-air ratchets you up it half a metre per frame.
      for (const axis of ['x', 'z'] as const) {
        const delta = this.velocity[axis] * dt;
        const before = this.pos[axis];
        const blocked = this.moveAxis(axis, delta, near);
        if (blocked && this.grounded && Math.abs(delta) > 1e-5) {
          const saved = this.pos.clone();
          this.pos[axis] = before;
          this.pos.y += this.step;
          if (this.overlaps(near).length === 0) {
            const stillBlocked = this.moveAxis(axis, delta, near);
            if (stillBlocked) this.pos.copy(saved);
          } else {
            this.pos.copy(saved);
          }
        }
      }
      this.mount(near);

      // Land on whatever we stepped onto. Without this the step-up leaves the
      // player floating half a metre above the tread, and the camera pops as
      // the smoothing drains — the exact jolt that makes stairs feel bad. The
      // same sweep is what keeps you on the ground walking *down* a slope
      // instead of launching off every slice.
      if (wasGrounded && this.velocity.y <= 0) {
        const beforeSnap = this.pos.y;
        // Ask whether *this sweep* found ground. Reading `grounded` directly
        // would see the flag already set by the gravity step earlier in the
        // frame, so a snap that found nothing would never be undone — and the
        // player would sink half a metre per frame until they fell through.
        const groundedBefore = this.grounded;
        this.grounded = false;
        this.moveVertical(-(this.step + 0.02), near);
        if (!this.grounded) {
          this.pos.y = beforeSnap;
          this.grounded = groundedBefore;
        }
      } else {
        this.moveVertical(-0.001, near);
      }

      // Absorb the rise into a visual offset that decays, so climbing a step
      // does not snap the camera. Only upward, only within a step, and only
      // when grounded — a fall must read as a fall.
      const rise = this.pos.y - startY;
      if (this.grounded && rise > 1e-4 && rise <= this.step + 1e-3) {
        this.stepOffset = Math.min(this.step, this.stepOffset + rise);
      }
    }
    this.stepOffset *= Math.exp(-dt / STEP_SMOOTH_TAU);

    // Remember the last place we were genuinely standing, and use it if the
    // world ever loses us.
    if (this.grounded) {
      this.lastSafe.copy(this.pos);
      this.hasSafe = true;
    } else if (this.hasSafe && this.pos.y < this.killPlane) {
      this.pos.copy(this.lastSafe);
      this.velocity.set(0, 0, 0);
      this.stepOffset = 0;
      this.grounded = true;
      this.rescues++;
    }

    const eye = this.crouching ? EYE.crouch : EYE.stand;
    this.camera.position.set(this.pos.x, this.pos.y + eye - this.stepOffset, this.pos.z);
    this.camera.quaternion.setFromEuler(new THREE.Euler(this.pitch, this.yaw, 0, 'YXZ'));
  }
}
